import {
  OfflineAccessSetupPayload,
  OfflineAccessSetupResponse,
  GetOfflineAccessSetupParams,
  OfflineAccessRequestPayload,
  OfflineAccessRequestResponse,
  OfflineAccessVerifyPayload,
  OfflineAccessVerifyResponse,
  OfflineAccessQuestionsParams,
  OfflineAccessQuestionPayload,
  OfflineAccessQuestionAnswerPayload,
  OfflineAccessQuestionVoteParams,
  OfflineAccessDirectMessagePayload,
  OfflineAccessDirectMessageParams,
  OfflineAccessDirectMessageItem,
} from "interfaces";
import { DiscussionInterface } from "interfaces/interfaces-data";
import instance from "lib/axiosInterceptor";

// 1. Configure offline access from the organizer setup UI.
export async function setupOfflineAccessService(
  payload: OfflineAccessSetupPayload,
): Promise<OfflineAccessSetupResponse> {
  const response = await instance.post<OfflineAccessSetupResponse>(
    "/OfflineAccess/Setup",
    payload,
  );
  return response.data;
}

// 2. Load the existing offline access config / public access form.
export async function getOfflineAccessSetupService(
  params: GetOfflineAccessSetupParams,
): Promise<OfflineAccessSetupResponse> {
  const { resourceType, resourceId, eventId } = params;
  const response = await instance.get<OfflineAccessSetupResponse>(
    `/OfflineAccess/Setup/${resourceType}/${resourceId}`,
    {
      params: eventId ? { eventId } : undefined,
    },
  );
  return response.data;
}

// 3. Submit lead capture details for offline access.
export async function requestOfflineAccessService(
  payload: OfflineAccessRequestPayload,
): Promise<OfflineAccessRequestResponse> {
  const response = await instance.post<OfflineAccessRequestResponse>(
    "/OfflineAccess/Request",
    payload,
  );
  return response.data;
}

// 4. Verify the email OTP and obtain the presentaConnectionId.
export async function verifyOfflineAccessService(
  payload: OfflineAccessVerifyPayload,
): Promise<OfflineAccessVerifyResponse> {
  const response = await instance.post<OfflineAccessVerifyResponse>(
    "/OfflineAccess/Verify",
    payload,
  );
  return response.data;
}

// 5. Load questions for an offline-access resource.
export async function getOfflineAccessQuestionsService(
  params: OfflineAccessQuestionsParams,
): Promise<DiscussionInterface[]> {
  const { resourceType, resourceId, presentaConnectionId, slideId } = params;
  const response = await instance.get<DiscussionInterface[]>(
    `/OfflineAccess/Questions/${resourceType}/${resourceId}`,
    {
      params: {
        ...(presentaConnectionId ? { presentaConnectionId } : {}),
        ...(slideId ? { slideId } : {}),
      },
    },
  );
  return response.data;
}

// 6. Ask a question for an offline-access resource.
export async function postOfflineAccessQuestionService(
  params: OfflineAccessQuestionsParams,
  payload: OfflineAccessQuestionPayload,
): Promise<DiscussionInterface> {
  const response = await instance.post<DiscussionInterface>(
    `/OfflineAccess/Questions/${params.resourceType}/${params.resourceId}`,
    payload,
  );
  return response.data;
}

// 7. Answer a question for an offline-access resource.
export async function answerOfflineAccessQuestionService(
  payload: OfflineAccessQuestionAnswerPayload,
): Promise<DiscussionInterface> {
  const response = await instance.post<DiscussionInterface>(
    `/OfflineAccess/Questions/${payload.resourceType}/${payload.resourceId}/${payload.questionId}/Answer`,
    { answer: payload.answer },
  );
  return response.data;
}

// 8. Upvote a question. `valid: true` records an up-vote; use
// removeOfflineAccessQuestionVoteService to take it back.
export async function voteOfflineAccessQuestionService(
  params: OfflineAccessQuestionVoteParams,
): Promise<void> {
  const { resourceType, resourceId, questionId, presentaConnectionId } = params;
  await instance.post(
    `/OfflineAccess/Questions/${resourceType}/${resourceId}/${questionId}/Vote`,
    { presentaConnectionId, valid: true },
  );
}

// 9. Remove a previously cast vote from a question.
export async function removeOfflineAccessQuestionVoteService(
  params: OfflineAccessQuestionVoteParams,
): Promise<void> {
  const { resourceType, resourceId, questionId, presentaConnectionId } = params;
  await instance.delete(
    `/OfflineAccess/Questions/${resourceType}/${resourceId}/${questionId}/Vote`,
    { params: { presentaConnectionId } },
  );
}

// 10. Send a direct message to the link owner.
export async function sendOfflineAccessDirectMessageService(
  payload: OfflineAccessDirectMessagePayload,
): Promise<void> {
  const { resourceType, resourceId, presentaConnectionId, message } = payload;
  await instance.post(
    `/OfflineAccess/DirectMessage/${resourceType}/${resourceId}`,
    { presentaConnectionId, message },
  );
}

// 11. Fetch the direct-message thread (same path as the POST, GET variant).
export async function getOfflineAccessDirectMessagesService(
  params: OfflineAccessDirectMessageParams,
): Promise<OfflineAccessDirectMessageItem[]> {
  const { resourceType, resourceId, presentaConnectionId } = params;
  const response = await instance.get<OfflineAccessDirectMessageItem[]>(
    `/OfflineAccess/DirectMessage/${resourceType}/${resourceId}`,
    {
      params: {
        ...(presentaConnectionId ? { presentaConnectionId } : {}),
      },
    },
  );
  return response.data;
}
